import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CATALOG_FAMILIES, type CatalogFamily } from "../../content/catalogFamilies.ts";
import { CLUB_SAMPLE, CLUB_SHARE, otherSideOf } from "../../content/openingContext.ts";
import { listCourses, type CourseSummary } from "../../content/lookup.ts";
import { loadCourseCopy, loadedOpeningGuide } from "../../content/catalogCopy.ts";
import type { OpeningGuide } from "../../content/openingGuides.ts";
import { usePageMeta } from "../seo.ts";
import { LevelTag } from "../openings/LevelTag.tsx";
import { OpeningInfographic } from "../openings/OpeningInfographic.tsx";
import { pieceSetById } from "../../appearance/appearance.ts";
import { useAppearance } from "../../appearance/useAppearance.ts";

const SIDE_NAME = { white: "White", black: "Black" } as const;

function familyAnchor(family: CatalogFamily): string {
  return `family-${family.id}`;
}

function cardAnchor(course: CourseSummary): string {
  return `course-${course.slug}`;
}

/** The opening moves, or a quiet stand-in while that guide downloads. */
function OpeningPreview({
  course,
  guide,
  pending,
}: {
  course: CourseSummary;
  guide: OpeningGuide | null;
  pending: boolean;
}) {
  if (guide) {
    return (
      <OpeningInfographic layout="card" guide={guide} side={course.side} title={course.title} />
    );
  }
  if (!pending) return null;
  return <div className="opening-card-diagram-pending" aria-hidden="true" />;
}

function OpeningCard({
  course,
  other,
  guide,
  pending,
}: {
  course: CourseSummary;
  /** The course that teaches the same opening from the other color, if any. */
  other: CourseSummary | null;
  guide: OpeningGuide | null;
  pending: boolean;
}) {
  const share = CLUB_SHARE[course.openingId];
  const forSide = course.side === "black" ? " for Black" : "";
  return (
    <article className="opening-card" id={cardAnchor(course)}>
      <div className="opening-card-top">
        <span className="opening-card-eco" title="ECO opening code">
          {course.eco}
        </span>
        {course.limitedCoverage ? (
          <span className="coverage-chip">Limited book coverage</span>
        ) : null}
        <LevelTag level={course.level} />
      </div>
      <h5 className="opening-card-name">{course.title}</h5>
      <OpeningPreview course={course} guide={guide} pending={pending} />
      <p className="opening-card-desc">{course.tagline}</p>
      <div className="opening-card-facts">
        <p className="opening-card-focus">
          <span className="opening-focus-label">Focus</span>
          {course.focus}
        </p>
        {share ? (
          <p className="opening-card-club">
            <strong className="opening-card-club-figure">{share.figure}</strong> {share.caption}
          </p>
        ) : null}
        {other ? (
          <p className="opening-card-other">
            Also taught as {SIDE_NAME[other.side]}:{" "}
            <a href={`#${cardAnchor(other)}`}>{other.title}</a>
          </p>
        ) : null}
      </div>
      <div className="opening-card-actions">
        <Link
          className="btn-ghost btn-sm"
          to={`/opening/${course.slug}`}
          aria-label={`${course.title} theory${forSide}`}
        >
          Theory
        </Link>
        <Link
          className="btn-primary btn-sm"
          to={`/train/${course.slug}?mode=learn`}
          aria-label={`Train ${course.title}${course.side === "black" ? " as Black" : ""}`}
        >
          Train
        </Link>
      </div>
    </article>
  );
}

/** The side's king, drawn in the player's chosen piece set. */
function SideKing({ side }: { side: "white" | "black" }) {
  const pieces = pieceSetById(useAppearance().pieces);
  return (
    <svg className="side-king" viewBox="0 0 40 40" aria-hidden="true">
      <use href={`${pieces.file}#${side === "white" ? "w" : "b"}k`} />
    </svg>
  );
}

const SIDE_INTRO = {
  white:
    "New to openings? The Italian Game is the gentlest start: c3, d3 and castling before anything opens. The groups answer 1…e5 first, then the other replies to 1.e4, then 1.d4 and the flank openings.",
  black:
    "Defenses are grouped by White’s first move: 1.e4 first, then 1.d4, 1.c4 and 1.Nf3. Several are a White course seen from the other side, and the card says which.",
} as const;

export function CatalogPage() {
  const courses = listCourses();
  const [copyState, setCopyState] = useState<"loading" | "ready" | "error">(() => {
    const first = courses[0];
    return first && loadedOpeningGuide(first.openingId) ? "ready" : "loading";
  });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    loadCourseCopy().then(
      () => live && setCopyState("ready"),
      () => live && setCopyState("error")
    );
    return () => {
      live = false;
    };
  }, [attempt]);
  usePageMeta({
    title: "Openings — The First 15 Moves",
    description:
      "Choose a White opening or a Black defense. Courses that share a position are grouped, and each card shows the opening’s first moves on a board, what the course trains and how common the line is in club play.",
    path: "/openings",
  });

  const byId = new Map(courses.map((course) => [course.openingId, course]));
  const count = (side: "white" | "black") =>
    courses.filter((course) => course.side === side).length;

  return (
    <section id="openings" className="panel openings-panel">
      <div className="section-head">
        <p className="eyebrow">Openings</p>
        <h2>Find your opening</h2>
        <p className="section-sub">
          Pick a White opening or a Black defense. Each card shows the opening’s first moves on a
          board: tap a move to see the position and what it is for. The arrow is the move, and the
          gold rings mark the squares it is about.
        </p>
        <ul className="catalog-facts" aria-label="What the courses include">
          <li>
            <strong>{courses.length}</strong> courses
          </li>
          <li>
            <strong>{count("white")}</strong> as White
          </li>
          <li>
            <strong>{count("black")}</strong> as Black
          </li>
          <li className="catalog-facts-depth">
            Each taught to <strong>move 15</strong>, side lines and traps included
          </li>
        </ul>
        {copyState === "error" ? (
          <p className="opening-diagrams-error" role="alert">
            The move diagrams couldn’t load.{" "}
            <button
              className="btn-ghost btn-sm"
              type="button"
              onClick={() => {
                setCopyState("loading");
                setAttempt((n) => n + 1);
              }}
            >
              Try again
            </button>
          </p>
        ) : null}
        {copyState === "loading" ? (
          <p className="sr-only" role="status">
            Loading the move diagrams…
          </p>
        ) : null}
      </div>
      <nav className="catalog-jump" aria-label="Jump to a group of openings">
        {(["white", "black"] as const).map((side) => (
          <div className="catalog-jump-row" key={side}>
            <span className="catalog-jump-side">{SIDE_NAME[side]}</span>
            <ul className="catalog-jump-list">
              {CATALOG_FAMILIES.filter((family) => family.side === side).map((family) => (
                <li key={family.id}>
                  <a className="catalog-jump-link" href={`#${familyAnchor(family)}`}>
                    {family.short}
                    <span
                      className="catalog-jump-count"
                      aria-label={`${family.openingIds.length} courses`}
                    >
                      {family.openingIds.length}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      {(["white", "black"] as const).map((side) => (
        <section className="opening-side-group" key={side} aria-labelledby={`openings-${side}`}>
          <h3 id={`openings-${side}`}>
            <SideKing side={side} />
            Play as {SIDE_NAME[side]}
            <span className="opening-side-count">{count(side)} courses</span>
          </h3>
          <p className="section-sub">{SIDE_INTRO[side]}</p>
          <div id={side === "white" ? "openingCards" : "blackOpeningCards"}>
            {CATALOG_FAMILIES.filter((family) => family.side === side).map((family) => (
              <section
                key={family.id}
                id={familyAnchor(family)}
                className="opening-family"
                aria-labelledby={`${familyAnchor(family)}-title`}
              >
                <header className="opening-family-head">
                  <p className="opening-family-against">{family.against}</p>
                  <h4 id={`${familyAnchor(family)}-title`} className="opening-family-title">
                    {family.title}
                  </h4>
                  <p className="opening-family-note">{family.note}</p>
                </header>
                <div className="opening-cards">
                  {family.openingIds.map((id) => {
                    const course = byId.get(id);
                    if (!course) return null;
                    const otherId = otherSideOf(id);
                    return (
                      <OpeningCard
                        key={course.slug}
                        course={course}
                        other={otherId ? byId.get(otherId) || null : null}
                        guide={copyState === "ready" ? loadedOpeningGuide(course.openingId) : null}
                        pending={copyState === "loading"}
                      />
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </section>
      ))}
      <p className="opening-sample-note catalog-sample-note">{CLUB_SAMPLE}</p>
    </section>
  );
}
